import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';

const AI_BASE_URL = 'https://transcoder.gizmott.com/ai/api';

const formatLegacyDate = (value) => {
  if (!value) return '';
  // Only swap dashes for plain YYYY-MM-DD strings (avoids UTC parsing); ISO datetimes parse as-is
  const dateObj = new Date(
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.replace(/-/g, '/') : value
  );
  if (Number.isNaN(dateObj.getTime())) return '';
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getLegacyReportHeaders = (pubId, channelId) => {
  const roleId = localStorage.getItem('role_id');
  const accessToken = localStorage.getItem('access-token');
  const contentPartnerId = localStorage.getItem('contentPartnerId');

  if (roleId === '7') {
    return {
      'access-token': accessToken,
      role_id: roleId
    };
  }

  if (roleId === '8') {
    return {
      'access-token': accessToken,
      pubid: pubId,
      ...(channelId ? { channelid: channelId } : {}),
      ...(contentPartnerId ? { content_partner_id: contentPartnerId } : {})
    };
  }

  return {
    'access-token': accessToken,
    pubid: pubId,
    ...(channelId ? { channelid: channelId } : {})
  };
};

const getCompletedVideoLegacyRequestData = ({ pubId, filter, channel }) => {
  const defaultEndDate = formatLegacyDate(new Date());
  const defaultStartDate = formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
  let startDate = formatLegacyDate(filter?.start_date) || defaultStartDate;
  let endDate = formatLegacyDate(filter?.end_date) || defaultEndDate;

  if (startDate > endDate) {
    const temp = startDate;
    startDate = endDate;
    endDate = temp;
  }

  const params = {};
  const effectivePubId = pubId || filter?.publishers?.pubid || localStorage.getItem('pub_id');

  if (filter?.isCategorySelected) params.categorychecked = 1;
  if (filter?.isAppSelected) params.appchecked = 1;
  if (filter?.isChannelsSelected) params.channelchecked = 1;
  if (filter?.isCountrySelected) params.countrychecked = 1;
  if (filter?.isPublisherSelected || effectivePubId) params.publisherchecked = 1;
  if (effectivePubId) params.publisher_id = effectivePubId;
  if (filter?.appType?.id) params.app_id = filter.appType.id;
  if (filter?.category?.category_id) params.category_id = filter.category.category_id;
  if (filter?.percentage?.value) params.percentage = filter.percentage.value;
  if (filter?.isMonthlySelected) params.monthly_checked = 1;
  if (filter?.isAggregateSelected) params.aggregate_checked = 1;
  if (filter?.isCitySelected) params.citychecked = 1;

  const selectedChannel = Array.isArray(channel) && channel?.[0]?.channel_id
    ? channel[0]
    : filter?.channel;
  if (selectedChannel?.channel_id) {
    params.channelchecked = 1;
    params.channel_id = selectedChannel.channel_id;
  }

  if (filter?.country?.label && filter.country.label !== 'All') {
    params.country = filter.country.label;
  }

  if (filter?.city?.label && filter.city.label !== 'All') {
    params.city = filter.city.label;
  }

  const defaultContentPartnerId = localStorage.getItem('contentPartnerId');
  if (defaultContentPartnerId) {
    params.content_partner_id = defaultContentPartnerId;
  }

  const roleId = localStorage.getItem('role_id');
  if (roleId) {
    params.role_id = roleId;
  }

  if (filter?.isContentPartnerSelected) {
    params.content_partner_checked = 1;
    if (filter?.contentPartner?.content_partner_id) {
      params.content_partner_id = filter.contentPartner.content_partner_id;
    }
  }

  return {
    startDate,
    endDate,
    params,
    effectivePubId
  };
};

const buildTrialConversionsQueryString = ({
  startDate,
  endDate,
  subscriptionIds = [],
  deviceTypes = [],
  paymentModes = [],
  countries = [],
  countryCodes = [],
  includeCountry = false,
  includePaymentMethod = false,
  status,
  search,
  page,
  pageSize,
} = {}) => {
  const params = new URLSearchParams();

  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (subscriptionIds.length) params.append('subscription_ids', subscriptionIds.join(','));
  if (deviceTypes.length) params.append('device_types', deviceTypes.join(','));
  if (paymentModes.length) params.append('mode_of_payment', paymentModes.join(','));
  if (countries.length) params.append('countries', countries.join(','));
  if (countryCodes.length) params.append('country_codes', countryCodes.join(','));
  if (includeCountry) params.append('include_country', '1');
  if (includePaymentMethod) params.append('include_payment_method', '1');
  if (status) params.append('status', status);
  if (search) params.append('search', search);
  if (page) params.append('page', String(page));
  if (pageSize) params.append('page_size', String(pageSize));

  return params.toString();
};

const EMPTY_TRIAL_COMPARISON = {
  previousRange: {},
  previousSummary: {},
  periodLengthDays: 0,
  metrics: {},
};

const EMPTY_TRIAL_CONVERSION_RESPONSE = {
  summary: {},
  range: {},
  planBreakdown: [],
  deviceBreakdown: [],
  cancellationTiming: [],
  statusComposition: [],
  insights: {},
  comparison: EMPTY_TRIAL_COMPARISON,
  userList: [],
};

const getContentPartnerRevenueLegacyRequestData = ({ pubId, data = {}, channels, isDefault = false, includeChannel = true }) => {
  const roleId = localStorage.getItem('role_id');
  const pubIdFromStorage = localStorage.getItem('pub_id');
  const effectivePubId = data?.publishers?.pubid || pubId || pubIdFromStorage;
  const selectedChannel = Array.isArray(channels)
    ? channels[0]
    : (data?.channel?.channel_id ? data.channel : null);

  let startDate = isDefault
    ? moment().subtract(6, 'day').format('YYYY-MM-DD')
    : moment(data?.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
  let endDate = isDefault
    ? moment().format('YYYY-MM-DD')
    : moment(data?.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD');

  if (!startDate || startDate === 'Invalid date') {
    startDate = moment().subtract(6, 'day').format('YYYY-MM-DD');
  }

  if (!endDate || endDate === 'Invalid date') {
    endDate = moment().format('YYYY-MM-DD');
  }

  if (startDate > endDate) {
    const tempDate = startDate;
    startDate = endDate;
    endDate = tempDate;
  }

  const params = new URLSearchParams({
    startDate,
    endDate,
    publisherchecked: '1',
    publisher_id: String(effectivePubId || ''),
    role_id: String(roleId || '')
  });

  if (includeChannel && selectedChannel?.channel_id) {
    params.append('channelchecked', '1');
    params.append('channel_id', selectedChannel.channel_id);
  }

  if (!isDefault) {
    if (data?.isAppSelected) {
      params.append('appchecked', '1');
      if (data?.appType?.id) {
        params.append('app_id', data.appType.id);
      }
    }

    if (data?.isCountrySelected) {
      params.append('countrychecked', '1');
      if (data?.country?.value && data.country.value !== 1) {
        params.append('country', data.country.label);
      }
    }

    if (data?.isCitySelected) {
      params.append('citychecked', '1');
      if (data?.city?.label && data.city.label !== 'All') {
        params.append('city', data.city.label);
      }
    }

    if (data?.isContentPartnerSelected) {
      params.append('content_partner_checked', '1');
      if (data?.contentPartner?.content_partner_id) {
        params.append('content_partner_id', data.contentPartner.content_partner_id);
      }
    }

    if (data?.subType?.subscription_type_id) {
      params.append('subscription_type', data.subType.subscription_type_id);
    }

    if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
      params.append('mode_of_payment', `'${data.paymentMode.label}'`);
    }

    params.append('showchecked', '1');
    if (data?.show?.id) {
      params.append('show_id', data.show.id);
    }

    params.append('videochecked', '1');
    if (data?.video?.id) {
      params.append('video_id', data.video.id);
    }

    if (data?.isMonthlySelected) {
      params.append('monthly_checked', '1');
    }

    if (data?.isAggregateSelected) {
      params.append('aggregate_checked', '1');
    }
  }

  if (roleId === '8' && localStorage.getItem('contentPartnerId')) {
    params.append('content_partner_checked', '1');
    params.append('content_partner_id', localStorage.getItem('contentPartnerId'));
  }

  return {
    effectivePubId,
    selectedChannel,
    params
  };
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_SERVER_HOST,
  prepareHeaders: (headers) => {
    const accessToken = localStorage.getItem('access-token');
    const pubId = localStorage.getItem('pub_id');
    if (accessToken) headers.set('access-token', accessToken);
    if (pubId) headers.set('pubid', pubId);
    return headers;
  }
});

const baseQueryWithToast = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  if (result.error) {
    const apiMessage = result.error?.data?.message;
    if (result.error.status !== 429) {
      toast.error(apiMessage || "Something went wrong");
    }
  }
  return result;
};

export const analyticsApi = createApi({
  reducerPath: 'analyticsApi',
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  keepUnusedDataFor: 0,
  baseQuery: baseQueryWithToast,
  tagTypes: ['Viewership', 'ManagedAppList', 'ManagedAppMeta', 'ManagedAppDetails', 'SubscribedUsersReport', 'RenewalReport', 'CancellationReport', 'PayPerViewReport', 'RevenueReport', 'RefundReport', 'DemandReport'],
  endpoints: (builder) => ({

    // VOD analytics report (viewed minutes, views) — combined prev+current range
    getVodAnalytics: builder.query({
      query: ({ pubId, channelId, startDate, endDate }) => {
        const roleId = localStorage.getItem('role_id');
        const contentPartnerId = localStorage.getItem('contentPartnerId');
        let url = `/report/videoAnalytics?startDate=${startDate}&endDate=${endDate}&publisherchecked=1&publisher_id=${pubId}`;
        if (channelId) url += `&channelchecked=1&channel_id=${channelId}`;
        if (roleId) url += `&role_id=${roleId}`;
        if (contentPartnerId) url += `&content_partner_checked=1&content_partner_id=${contentPartnerId}`;
        return { url, headers: { channelid: channelId || '' } };
      }
    }),

    // Active users over time — combined prev+current range
    getActiveUsers: builder.query({
      query: ({ pubId, channelId, startDate, endDate }) => {
        let url = `/report/activeUsers?startDate=${startDate}&endDate=${endDate}&publisherchecked=1&publisher_id=${pubId}`;
        if (channelId) url += `&channelchecked=1&channel_id=${channelId}`;
        return { url, headers: { channelid: channelId || '' } };
      }
    }),

    // Active users current-period stats (percentage vs previous)
    getActiveUsersStats: builder.query({
      query: ({ pubId, channelId, startDate, endDate }) => ({
        url: `/report/activeUsersStats?startDate=${startDate}&endDate=${endDate}&publisher_id=${pubId}&channel_id=${channelId}`,
        headers: { channelid: channelId || '' }
      })
    }),

    // New user registrations — supports optional channel/app/city/country filters
    getNewUsers: builder.query({
      query: ({
        pubId,
        startDate,
        endDate,
        channelId = "",
        publisherChecked = 1,
        appChecked = 0,
        app = "",
        appId = "",
        cityChecked = 0,
        city = "",
        countryChecked = 0,
        country = "",
        monthlyChecked = 0,
        aggregateChecked = 0,
        uniqueChecked = 0,
      }) => {
        let url = `/report/newUsers?startDate=${startDate}&endDate=${endDate}`;
        url += publisherChecked === 1 ? `&publisherchecked=1&publisher_id=${pubId}` : "";
        url += channelId ? `&channelchecked=1&channel_id=${channelId}` : "";
        url += appChecked === 1
          ? `&appchecked=1${appId ? `&app_id=${appId}` : ""}${app ? `&app_name=${encodeURIComponent(app)}` : ""}`
          : "";
        url += cityChecked === 1 ? `&citychecked=1${city ? `&city=${encodeURIComponent(city)}` : ""}` : "";
        url += countryChecked === 1
          ? `&countrychecked=1${country ? `&country=${encodeURIComponent(country)}` : ""}`
          : "";
        url += monthlyChecked === 1 ? `&monthly_checked=1` : "";
        url += aggregateChecked === 1 ? `&aggregate_checked=1` : "";
        url += uniqueChecked === 1 ? `&unique_checked=1` : "";

        return {
          url,
          headers: { channelid: channelId || "" },
        };
      }
    }),

    // Total user count
    getTotalUsers: builder.query({
      query: () => '/analytics/subscription/totalActive'
    }),

    // Live viewer count for a channel
    getLiveViewers: builder.query({
      query: ({ channelId }) => ({
        url: '/channel/livecount',
        headers: { channelid: channelId || '' }
      })
    }),

    // Top viewed shows / videos / shorts
    getTopViewed: builder.query({
      query: ({ pubId, roleId, channelId, startDate, endDate }) => ({
        url: `/home/topViewed?start_date=${startDate}&end_date=${endDate}`,
        headers: {
          role_id: roleId,
          ...(channelId ? { channelid: channelId } : {})
        }
      })
    }),

    // Legacy viewership analytics endpoints
    getViewershipTopViewed: builder.query({
      query: ({ startDate, endDate, channelId }) => ({
        url: `/analytics/viewership/topViewed?start_date=${startDate}&end_date=${endDate}`,
        headers: { channelid: channelId || '' }
      })
    }),

    getViewershipEngagementAndGenres: builder.query({
      query: ({ startDate, endDate, channelId }) => ({
        url: `/analytics/viewership/engagement?start_date=${startDate}&end_date=${endDate}`,
        headers: { channelid: channelId || '' }
      })
    }),

    getViewershipTopLiveChannels: builder.query({
      query: ({ startDate, endDate, channelId }) => ({
        url: `/analytics/viewership/liveChannels?start_date=${startDate}&end_date=${endDate}`,
        headers: { channelid: channelId || '' }
      })
    }),

    getViewershipActiveAndNewUsers: builder.query({
      query: ({ startDate, endDate, channelId }) => ({
        url: `/analytics/viewership/users?start_date=${startDate}&end_date=${endDate}`,
        headers: { channelid: channelId || '' }
      })
    }),

    getViewershipContentPerformance: builder.query({
      query: ({ startDate, endDate, channelId }) => ({
        url: `/analytics/viewership/content?start_date=${startDate}&end_date=${endDate}`,
        headers: { channelid: channelId || '' }
      })
    }),

    getViewershipLiveAnalyticsGraph: builder.query({
      query: ({ pubId, liveChannelId, startDate, endDate }) => ({
        url: `/report/linearAnalytics?startDate=${startDate}&endDate=${endDate}&publisherchecked=1&publisher_id=${pubId}&channelchecked=1&channel_id=${liveChannelId}`
      })
    }),

    getFastChannelsLegacy: builder.query({
      query: ({ channelId }) => {
        const roleId = localStorage.getItem('role_id');
        const pubId = localStorage.getItem('pub_id');
        const contentPartnerId = localStorage.getItem('contentPartnerId');

        return {
          url: '/channel/linear/list',
          headers: {
            'access-token': localStorage.getItem('access-token') || '',
            ...(pubId ? { pubid: pubId } : {}),
            ...(roleId === '8'
              ? (contentPartnerId ? { content_partner_id: contentPartnerId } : {})
              : (channelId ? { channelid: channelId } : {}))
          }
        };
      }
    }),

    getFastAnalyticsGraphLegacy: builder.query({
      query: ({ pubId, channels, filter }) => {
        let startDate = filter?.start_date
          ? moment(filter.start_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : moment().subtract(6, 'day').format('YYYY-MM-DD');
        let endDate = filter?.end_date
          ? moment(filter.end_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');

        if (!startDate || startDate === 'Invalid date') {
          startDate = moment().subtract(6, 'day').format('YYYY-MM-DD');
        }
        if (!endDate || endDate === 'Invalid date') {
          endDate = moment().format('YYYY-MM-DD');
        }
        if (startDate > endDate) {
          const temp = startDate;
          startDate = endDate;
          endDate = temp;
        }

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        const selectedChannel = Array.isArray(channels) && channels[0]
          ? channels[0]
          : (filter?.channel?.channel_id ? filter.channel : null);

        if (selectedChannel) {
          params.append('channelchecked', '1');
          if (selectedChannel?.channel_id) {
            params.append('channel_id', selectedChannel.channel_id);
          }
        }

        const contentPartnerId = localStorage.getItem('contentPartnerId');

        return {
          url: `/report/linearAnalytics?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token') || '',
            pubid: String(pubId || ''),
            ...(contentPartnerId ? { content_partner_id: contentPartnerId } : {})
          }
        };
      }
    }),

    getFastAnalyticsDefaultLegacy: builder.query({
      query: ({ pubId, channels }) => {
        const startDate = moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        const selectedChannel = Array.isArray(channels) && channels[0] ? channels[0] : null;
        if (selectedChannel) {
          params.append('channelchecked', '1');
          if (selectedChannel?.channel_id) {
            params.append('channel_id', selectedChannel.channel_id);
          }
        }

        const contentPartnerId = localStorage.getItem('contentPartnerId');

        return {
          url: `/report/linearAnalytics?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token') || '',
            pubid: String(pubId || ''),
            ...(contentPartnerId ? { content_partner_id: contentPartnerId } : {})
          }
        };
      }
    }),

    getFastAnalyticsFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const effectivePubId = data?.publishers?.pubid || pubId;
        const startDate = moment(data?.start_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD');
        const endDate = moment(data?.end_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD');

        const params = new URLSearchParams({
          publisherchecked: '1',
          publisher_id: String(effectivePubId || ''),
          startDate,
          endDate
        });

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.label) {
            params.append('country', data.country.label);
          }
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) params.append('app_id', data.appType.id);
          if (data?.appType?.label && data.appType.label !== 'All') {
            params.append('app_name', data.appType.label.trim());
          }
        }

        if (data?.isChannelSelected || data?.isChannelsSelected) {
          params.append('channelchecked', '1');
          if (data?.channel?.channel_id) {
            params.append('channel_id', data.channel.channel_id);
          } else if (data?.channel?.[0]?.channel_id) {
            params.append('channel_id', data.channel[0].channel_id);
          }
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isVideoChecked) {
          params.append('videochecked', '1');
          if (data?.video?.id) {
            params.append('video_id', data.video.id);
          }
        }

        if (data?.isMonthlySelected) {
          params.append('monthly_checked', '1');
        }

        if (data?.isAggregateSelected) {
          params.append('aggregate_checked', '1');
        }

        return {
          url: `/report/linearAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(
            effectivePubId,
            data?.channel?.channel_id || data?.channel?.[0]?.channel_id
          )
        };
      }
    }),

    getActiveUsersReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const effectivePubId = data?.publishers?.pubid || pubId;
        const startDate = formatLegacyDate(data?.start_date);
        const endDate = formatLegacyDate(data?.end_date);
        const isUniqueSelected = Boolean(data?.isUniqueSelected);
        const basePath = isUniqueSelected ? '/report/activeUsersAggregate' : '/report/activeUsers';

        const params = new URLSearchParams({
          publisherchecked: '1',
          publisher_id: String(effectivePubId || ''),
          startDate,
          endDate
        });

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.label) {
            params.append('country', data.country.label);
          }
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) params.append('app_id', data.appType.id);
          if (data?.appType?.label && data.appType.label !== 'All') {
            params.append('app_name', data.appType.label.trim());
          }
        }

        if (data?.isChannelSelected) {
          params.append('channelchecked', '1');
          if (data?.channel?.channel_id) {
            params.append('channel_id', data.channel.channel_id);
          }
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isMonthlySelected) {
          params.append('monthly_checked', '1');
        }

        if (data?.isAggregateSelected) {
          params.append('aggregate_checked', '1');
        }

        return {
          url: `${basePath}?${params.toString()}`,
          headers: getLegacyReportHeaders(effectivePubId, data?.channel?.channel_id)
        };
      }
    }),

    getUserDetailsReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const effectivePubId = data?.publishers?.pubid || pubId;
        const startDate = formatLegacyDate(data?.start_date);
        const endDate = formatLegacyDate(data?.end_date);

        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate
        });

        if (data?.isChannelSelected) {
          params.append('channel_checked', '1');
          if (data?.channel?.channel_id) {
            params.append('channel_id', data.channel.channel_id);
          }
        }

        if (data?.isLoginTypeChecked) {
          params.append('login_type_checked', '1');
          if (data?.loginType?.value && data.loginType.value !== 'all') {
            params.append('login_type', data.loginType.value);
          }
        }

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.code) {
            params.append('country_code', data.country.code);
          }
        }

        if (data?.isAppSelected) {
          if (data?.appType?.app_type) {
            params.append('app_id', data.appType.app_type);
            params.append('app_checked', '1');
          }
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isMonthlySelected) {
          params.append('monthly_checked', '1');
        }

        if (data?.isAggregateSelected) {
          params.append('aggregate_checked', '1');
        }

        return {
          url: `/report/userDetails?${params.toString()}`,
          headers: getLegacyReportHeaders(effectivePubId, data?.channel?.channel_id)
        };
      },
      transformResponse: (response) => response?.data || []
    }),

    getUserDetailsDefaultLegacy: builder.query({
      query: ({ pubId, channelId }) => {
        const today = formatLegacyDate(new Date());
        const params = new URLSearchParams({
          start_date: today,
          end_date: today
        });

        if (channelId) {
          params.append('channel_checked', '1');
          params.append('channel_id', channelId);
        }

        return {
          url: `/report/userDetails?${params.toString()}`,
          headers: getLegacyReportHeaders(pubId, channelId)
        };
      },
      transformResponse: (response) => response?.data || []
    }),

    getTotalActiveUsersGraphLegacy: builder.query({
      query: ({ pubId, channels, filter }) => {
        let startDate = formatLegacyDate(filter?.start_date) || formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        let endDate = formatLegacyDate(filter?.end_date) || formatLegacyDate(new Date());
        if (startDate > endDate) {
          const temp = startDate;
          startDate = endDate;
          endDate = temp;
        }

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData) {
          params.append('channelchecked', '1');
          if (channelData.channel_id) {
            params.append('channel_id', channelData.channel_id);
          }
        }

        return {
          url: `/report/activeUsers?${params.toString()}`,
          headers: { 'access-token': localStorage.getItem('access-token'), pubid: pubId }
        };
      }
    }),

    getTotalActiveUsersDefaultLegacy: builder.query({
      query: ({ pubId, channels }) => {
        const endDate = formatLegacyDate(new Date());
        const startDate = formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData) {
          params.append('channelchecked', '1');
          if (channelData.channel_id) {
            params.append('channel_id', channelData.channel_id);
          }
        }

        return {
          url: `/report/activeUsers?${params.toString()}`,
          headers: { 'access-token': localStorage.getItem('access-token'), pubid: pubId }
        };
      }
    }),

    getNewsReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const effectivePubId = data?.publishers?.pubid || pubId;
        const startDate = formatLegacyDate(data?.start_date);
        const endDate = formatLegacyDate(data?.end_date);

        const params = new URLSearchParams({
          publisherchecked: '1',
          publisher_id: String(effectivePubId || ''),
          role_id: String(localStorage.getItem('role_id') || ''),
          startDate,
          endDate
        });

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.label) {
            params.append('country', data.country.label);
          }
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) params.append('app_id', data.appType.id);
        }

        if (data?.isChannelSelected || data?.isChannelsSelected) {
          params.append('channelchecked', '1');
          if (data?.channel?.channel_id) {
            params.append('channel_id', data.channel.channel_id);
          }
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isNewsChecked) {
          params.append('newschecked', '1');
          if (data?.news?.id) {
            params.append('news_id', data.news.id);
          }
        }

        if (data?.isMonthlySelected) params.append('monthly_checked', '1');
        if (data?.isAggregateSelected) params.append('aggregate_checked', '1');

        return {
          url: `/report/newsAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(effectivePubId, data?.channel?.channel_id)
        };
      }
    }),

    getNewsAnalyticsDefaultLegacy: builder.query({
      query: ({ pubId, channels }) => {
        const endDate = formatLegacyDate(new Date());
        const startDate = formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || ''),
          role_id: String(localStorage.getItem('role_id') || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelData.channel_id);
        }

        return {
          url: `/report/newsAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(pubId, channelData?.channel_id)
        };
      }
    }),

    getNewsListLegacy: builder.query({
      query: ({ pubId, channelId }) => ({
        url: '/news/list',
        headers: getLegacyReportHeaders(pubId, channelId || localStorage.getItem('currentChannel'))
      })
    }),

    getShowListLegacy: builder.query({
      query: ({ pubId, channelId }) => {
        const roleId = localStorage.getItem('role_id');
        const contentPartnerId = localStorage.getItem('contentPartnerId');

        return {
          url: '/show/list?dropdown=1',
          headers: {
            ...getLegacyReportHeaders(pubId, channelId || localStorage.getItem('currentChannel')),
            ...(roleId ? { role_id: roleId } : {}),
            ...(contentPartnerId ? { content_partner_id: contentPartnerId } : {})
          }
        };
      }
    }),

    getShortsReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const effectivePubId = data?.publishers?.pubid || pubId;
        const startDate = formatLegacyDate(data?.start_date);
        const endDate = formatLegacyDate(data?.end_date);

        const params = new URLSearchParams({
          publisherchecked: '1',
          publisher_id: String(effectivePubId || ''),
          role_id: String(localStorage.getItem('role_id') || ''),
          startDate,
          endDate
        });

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.label) {
            params.append('country', data.country.label);
          }
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) params.append('app_id', data.appType.id);
        }

        if (data?.isChannelSelected || data?.isChannelsSelected) {
          params.append('channelchecked', '1');
          if (data?.channel?.channel_id) {
            params.append('channel_id', data.channel.channel_id);
          }
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isShortsChecked) {
          params.append('videochecked', '1');
          if (data?.video?.id) {
            params.append('video_id', data.video.id);
          }
        }

        if (data?.isMonthlySelected) params.append('monthly_checked', '1');
        if (data?.isAggregateSelected) params.append('aggregate_checked', '1');

        const contentPartnerId = localStorage.getItem('contentPartnerId');
        if (contentPartnerId) {
          params.append('content_partner_id', contentPartnerId);
        }

        return {
          url: `/report/shortsAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(effectivePubId, data?.channel?.channel_id)
        };
      }
    }),

    getShortsAnalyticsDefaultLegacy: builder.query({
      query: ({ pubId, channels }) => {
        const endDate = formatLegacyDate(new Date());
        const startDate = formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || ''),
          role_id: String(localStorage.getItem('role_id') || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelData.channel_id);
        }

        const contentPartnerId = localStorage.getItem('contentPartnerId');
        if (contentPartnerId) {
          params.append('content_partner_id', contentPartnerId);
        }

        return {
          url: `/report/shortsAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(pubId, channelData?.channel_id)
        };
      }
    }),

    getShortsAnalyticsGraphLegacy: builder.query({
      query: ({ pubId, channels, filter }) => {
        let startDate = formatLegacyDate(filter?.start_date) || formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        let endDate = formatLegacyDate(filter?.end_date) || formatLegacyDate(new Date());

        if (startDate > endDate) {
          const temp = startDate;
          startDate = endDate;
          endDate = temp;
        }

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || ''),
          role_id: String(localStorage.getItem('role_id') || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelData.channel_id);
        }

        if (filter?.isMonthlySelected || filter?.monthlychecked) params.append('monthly_checked', '1');
        if (filter?.isAggregateSelected || filter?.aggregatechecked) params.append('aggregate_checked', '1');

        const contentPartnerId = localStorage.getItem('contentPartnerId');
        if (contentPartnerId) {
          params.append('content_partner_id', contentPartnerId);
        }

        return {
          url: `/report/shortsAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(pubId, channelData?.channel_id)
        };
      }
    }),

    getShortsListLegacy: builder.query({
      query: ({ pubId, channelId }) => {
        const roleId = localStorage.getItem('role_id');
        const contentPartnerId = localStorage.getItem('contentPartnerId');

        return {
          url: '/shorts/list',
          headers: {
            ...getLegacyReportHeaders(pubId, channelId || localStorage.getItem('currentChannel')),
            ...(roleId ? { role_id: roleId } : {}),
            ...(contentPartnerId ? { content_partner_id: contentPartnerId } : {})
          }
        };
      }
    }),

    getErrorReportGraphLegacy: builder.query({
      query: ({ pubId, channels, filter }) => {
        let startDate = filter?.start_date
          ? moment(filter.start_date).format('YYYY-MM-DD')
          : moment().subtract(6, 'day').format('YYYY-MM-DD');
        let endDate = filter?.end_date
          ? moment(filter.end_date).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');
        if (startDate > endDate) {
          const t = startDate; startDate = endDate; endDate = t;
        }

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelData.channel_id);
        }

        return {
          url: `/report/error?${params.toString()}`,
          headers: { 'access-token': localStorage.getItem('access-token'), pubid: String(pubId || '') }
        };
      }
    }),

    getErrorReportDefaultLegacy: builder.query({
      query: ({ pubId, channels }) => {
        const startDate = moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelData.channel_id);
        }

        return {
          url: `/report/error?${params.toString()}`,
          headers: { 'access-token': localStorage.getItem('access-token'), pubid: String(pubId || '') }
        };
      }
    }),

    getErrorReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const startDate = data?.start_date ? moment(data.start_date).format('YYYY-MM-DD') : moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = data?.end_date ? moment(data.end_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
        const effectivePubId = data?.publishers?.pubid || pubId;

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(effectivePubId || '')
        });

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.label && data.country.label !== 'All') {
            params.append('country', data.country.label);
          }
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) params.append('app_id', data.appType.id);
        }

        if (data?.isChannelSelected) {
          params.append('channelchecked', '1');
          if (data?.channel?.channel_id) params.append('channel_id', data.channel.channel_id);
        }

        if (data?.isMonthlySelected) params.append('monthly_checked', '1');

        return {
          url: `/report/error?${params.toString()}`,
          headers: { 'access-token': localStorage.getItem('access-token'), pubid: String(effectivePubId || '') }
        };
      }
    }),

    getRatingReportDefault: builder.query({
      query: ({ pubId, channelId }) => ({
        url: '/report/ratings?videochecked=1&showchecked=1&collectionchecked=1',
        headers: {
          pubid: String(pubId || localStorage.getItem('pub_id') || ''),
          channelid: channelId || localStorage.getItem('currentChannel') || ''
        }
      })
    }),

    getRatingReportFilter: builder.query({
      query: ({ pubId, data }) => {
        const startDate = formatLegacyDate(data?.start_date) || formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        const endDate = formatLegacyDate(data?.end_date) || formatLegacyDate(new Date());
        const effectivePubId = data?.publishers?.pubid || pubId || localStorage.getItem('pub_id');
        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(effectivePubId || '')
        });

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.code) params.append('country_code', data.country.code);
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) params.append('app_id', data.appType.id);
        }

        if (data?.isChannelSelected && data?.channel?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', data.channel.channel_id);
        }

        if (data?.isShowchecked) {
          params.append('showchecked', '1');
          if (!data?.isVideoChecked && data?.show?.id) params.append('show_id', data.show.id);
        }

        if (data?.isVideoChecked) {
          params.append('videochecked', '1');
          if (!data?.isShowchecked && data?.video?.id) params.append('show_id', data.video.id);
        }

        if (data?.isCollectionchecked) {
          params.append('collectionchecked', '1');
          if (!data?.isVideoChecked && data?.collection?.id) params.append('show_id', data.collection.id);
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') params.append('city', data.city.label);
        }

        if (data?.isMonthlySelected) params.append('monthly_checked', '1');
        if (data?.isAggregateSelected) params.append('aggregate_checked', '1');

        return {
          url: `/report/ratings?${params.toString()}`,
          headers: {
            pubid: String(effectivePubId || ''),
            channelid: data?.channel?.channel_id || localStorage.getItem('currentChannel') || ''
          }
        };
      }
    }),

    getShowDropdown: builder.query({
      query: ({ pubid }) => ({
        url: '/show/list?dropdown=1',
        headers: {
          pubid,
          channelid: localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      })
    }),

    getVideoDropdown: builder.query({
      query: ({ pubid }) => ({
        url: '/video/list?type=schedule_dropdown',
        headers: {
          pubid,
          channelid: localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      })
    }),

    getCollectionDropdown: builder.query({
      query: ({ pubid }) => ({
        url: '/collection/list?dropdown=1',
        headers: {
          pubid,
          channelid: localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      })
    }),

    getFavoriteReportDefault: builder.query({
      query: ({ pubId, channelId }) => ({
        url: '/report/favorite?videochecked=1&showchecked=1&collectionchecked=1',
        headers: {
          pubid: String(pubId || localStorage.getItem('pub_id') || ''),
          channelid: channelId || localStorage.getItem('currentChannel') || ''
        }
      })
    }),

    getFavoriteReportFilter: builder.query({
      query: ({ pubId, data }) => {
        const startDate = formatLegacyDate(data?.start_date) || formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        const endDate = formatLegacyDate(data?.end_date) || formatLegacyDate(new Date());
        const effectivePubId = data?.publishers?.pubid || pubId || localStorage.getItem('pub_id');
        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(effectivePubId || '')
        });

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.code) params.append('country_code', data.country.code);
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) params.append('app_id', data.appType.id);
        }

        if (data?.isChannelSelected && data?.channel?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', data.channel.channel_id);
        }

        if (data?.isShowchecked) {
          params.append('showchecked', '1');
          if (!data?.isVideoChecked && data?.show?.id) params.append('show_id', data.show.id);
        }

        if (data?.isVideoChecked) {
          params.append('videochecked', '1');
          if (!data?.isShowchecked && data?.video?.id) params.append('show_id', data.video.id);
        }

        if (data?.isCollectionchecked) {
          params.append('collectionchecked', '1');
          if (!data?.isVideoChecked && data?.collection?.id) params.append('show_id', data.collection.id);
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') params.append('city', data.city.label);
        }

        if (data?.isAggregateSelected) params.append('aggregate_checked', '1');

        return {
          url: `/report/favorite?${params.toString()}`,
          headers: {
            pubid: String(effectivePubId || ''),
            channelid: data?.channel?.channel_id || localStorage.getItem('currentChannel') || ''
          }
        };
      }
    }),

    getNewsLetterReportDefault: builder.query({
      query: ({ pubId, channelId }) => {
        const endDate = formatLegacyDate(new Date());
        const startDate = formatLegacyDate(new Date(new Date().setMonth(new Date().getMonth() - 1)));
        const params = new URLSearchParams({
          from: startDate,
          to: endDate
        });

        if (channelId) {
          params.append('channel_checked', '1');
          params.append('channel_id', channelId);
        }

        return {
          url: `/report/newsLetter?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token') || '',
            pubid: String(pubId || localStorage.getItem('pub_id') || ''),
            ...(channelId ? { channelid: channelId } : {})
          }
        };
      }
    }),

    getNewsLetterReportFilter: builder.query({
      query: ({ pubId, data }) => {
        const from = formatLegacyDate(data?.start_date) || formatLegacyDate(new Date(new Date().setMonth(new Date().getMonth() - 1)));
        const to = formatLegacyDate(data?.end_date) || formatLegacyDate(new Date());
        const params = new URLSearchParams({
          from,
          to
        });

        return {
          url: `/report/newsLetter?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token') || '',
            pubid: String(pubId || localStorage.getItem('pub_id') || '')
          }
        };
      }
    }),

    getDataUsageReportDefault: builder.query({
      query: ({ pubId, channelId, isMonthlySelected }) => {
        const endDate = moment().format('YYYY-MM-DD');
        const startDate = moment().subtract(1, 'month').format('YYYY-MM-DD');
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate
        });

        if (isMonthlySelected) {
          params.append('monthly_checked', '1');
        }

        if (channelId) {
          params.append('channel_checked', '1');
          params.append('channel_id', channelId);
        }

        return {
          url: `/report/cdnTraffic?${params.toString()}`,
          headers: localStorage.getItem('role_id') === '7'
            ? {
                'access-token': localStorage.getItem('access-token') || '',
                role_id: localStorage.getItem('role_id') || ''
              }
            : {
                'access-token': localStorage.getItem('access-token') || '',
                pubid: String(pubId || localStorage.getItem('pub_id') || ''),
                channel_id: localStorage.getItem('currentChannel') || ''
              }
        };
      }
    }),

    getDataUsageReportFilter: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams({
          start_date: moment(data?.start_date, ['YYYY-MM-DD', 'MM-DD-YYYY']).format('YYYY-MM-DD'),
          end_date: moment(data?.end_date, ['YYYY-MM-DD', 'MM-DD-YYYY']).format('YYYY-MM-DD')
        });

        if (data?.channel?.channel_id) {
          params.append('channel_checked', '1');
          params.append('channel_id', data.channel.channel_id);
        }

        if (data?.publisher && data?.publisher?.label !== 'All' && data?.publisher?.value) {
          params.append('pubid', data.publisher.value);
        }

        if (data?.isCountrySelected && data?.country?.value && data.country.value !== 1 && data?.country?.code) {
          params.append('country_checked', '1');
          params.append('country', data.country.code);
        }

        if (data?.isCitySelected && data?.city?.label && data.city.label !== 'All') {
          params.append('citychecked', '1');
          params.append('city', data.city.label);
        }

        if (data?.isMonthlySelected) {
          params.append('monthly_checked', '1');
        }

        return {
          url: `/report/cdnTraffic?${params.toString()}`,
          headers: localStorage.getItem('role_id') === '7'
            ? {
                'access-token': localStorage.getItem('access-token') || '',
                role_id: localStorage.getItem('role_id') || ''
              }
            : {
                'access-token': localStorage.getItem('access-token') || '',
                pubid: String(pubId || localStorage.getItem('pub_id') || ''),
                channel_id: localStorage.getItem('currentChannel') || ''
              }
        };
      }
    }),

    // Analytics filter endpoints (used by src/components/_analytics/filters.js)
    getAnalyticsFilterAppType: builder.query({
      query: ({ pubid, channelid }) => ({
        url: '/app/list',
        headers: {
          pubid,
          ...(channelid ? { channelid } : {})
        }
      })
    }),

    getAnalyticsFilterSubscriptionList: builder.query({
      query: () => ({
        url: '/subscription/active',
        headers: { pubid: localStorage.getItem('pub_id') }
      })
    }),

    getAnalyticsFilterSubscriptionType: builder.query({
      query: () => ({
        url: '/subscription/type',
        headers: { pubid: localStorage.getItem('pub_id') }
      })
    }),

    getAnalyticsFilterShowList: builder.query({
      query: ({ pubid, channelid }) => ({
        url: '/show/list',
        headers: {
          pubid,
          ...(channelid ? { channelid } : {})
        }
      })
    }),

    getAnalyticsFilterCountryList: builder.query({
      query: () => ({
        url: '/country/list?type=user_dropdown'
      })
    }),

    getAnalyticsFilterVideoList: builder.query({
      query: ({ pubid, channelid } = {}) => ({
        url: '/video/list?dropdown=1',
        headers: {
          pubid: pubid || localStorage.getItem('pub_id') || '',
          channelid: channelid || localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      })
    }),

    getAnalyticsFilterSubscriptionPlanList: builder.query({
      query: ({ pubid, channelid } = {}) => ({
        url: '/subscription/list',
        headers: {
          'access-token': localStorage.getItem('access-token'),
          pubid: pubid || localStorage.getItem('pub_id') || '',
          channelid: channelid || localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      })
    }),

    getAnalyticsFilterChannelList: builder.query({
      query: ({ pub_id }) => ({
        url: `/channel/list/${localStorage.getItem('pub_id')}`,
        headers: {
          pub_id
        }
      })
    }),

    getAnalyticsFilterPublishersList: builder.query({
      query: () => ({
        url: '/publisher/list'
      })
    }),

    getAnalyticsFilterCityList: builder.query({
      query: ({ country, search, limit = 100, offset = 0 } = {}) => ({
        url: '/country/cities',
        params: {
          ...(country ? { country } : {}),
          ...(search ? { search } : {}),
          limit,
          offset
        }
      })
    }),

    getAnalyticsFilterCategoryList: builder.query({
      query: ({ pubid }) => ({
        url: '/category/list',
        headers: { pubid },
        params: pubid ? { publisher_id: pubid } : {}
      })
    }),

    getAnalyticsFilterDemandSourceList: builder.query({
      query: ({ pub_id }) => ({
        url: '/demand/list?partner_id=',
        headers: { pub_id }
      })
    }),

    getAnalyticsFilterCouponsList: builder.query({
      query: ({ channelid }) => ({
        url: '/coupon/list',
        headers: {
          pubid: localStorage.getItem('pub_id'),
          ...(channelid ? { channelid } : {})
        }
      })
    }),

    // Legacy AVOD analytics endpoints
    getAvodTopDemandLegacy: builder.query({
      query: ({ startDate, endDate, channelId }) => ({
        url: `/analytics/avod/demands?start_date=${startDate}&end_date=${endDate}`,
        headers: { channelid: channelId || '' }
      })
    }),

    getAvodRevenueLegacy: builder.query({
      query: ({ startDate, endDate, channelId }) => ({
        url: `/analytics/avod/revenue?start_date=${startDate}&end_date=${endDate}`,
        headers: { channelid: channelId || '' }
      })
    }),

    // Legacy subscription analytics endpoints
    getSubscriptionTotalActiveLegacy: builder.query({
      query: () => ({
        url: '/analytics/subscription/totalActive'
      })
    }),

    getSubscriptionRevenueLegacy: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/analytics/subscription/revenue?start_date=${startDate}&end_date=${endDate}`
      })
    }),

    getSubscriptionReportByTypeLegacy: builder.query({
      query: ({ startDate, endDate, type }) => ({
        url: `/analytics/subscription/report?start_date=${startDate}&end_date=${endDate}&type=${type}`
      })
    }),

    getSubscriptionCancelledByTermsLegacy: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/analytics/subscription/churn?start_date=${startDate}&end_date=${endDate}`
      })
    }),

    getSubscriptionPayPerViewLegacy: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/analytics/subscription/payperview?start_date=${startDate}&end_date=${endDate}`
      })
    }),

    getSubscriptionTrialConversions: builder.query({
      query: ({
        startDate,
        endDate,
        subscriptionIds = [],
        deviceTypes = [],
        countries = [],
        countryCodes = [],
      } = {}) => {
        return {
          url: `/analytics/subscription/trial-conversions?${buildTrialConversionsQueryString({
            startDate,
            endDate,
            subscriptionIds,
            deviceTypes,
            countries,
            countryCodes,
          })}`
        };
      },
      transformResponse: (response) => response?.data || EMPTY_TRIAL_CONVERSION_RESPONSE
    }),

    getSubscriptionTrialConversionsEffectiveness: builder.query({
      query: (params = {}) => ({
        url: `/analytics/subscription/trial-conversions/effectiveness?${buildTrialConversionsQueryString(params)}`
      }),
      transformResponse: (response) => response?.data || {
        planBreakdown: [],
        statusCounts: {},
      }
    }),

    getSubscriptionTrialConversionsOverview: builder.query({
      query: (params = {}) => ({
        url: `/analytics/subscription/trial-conversions/overview?${buildTrialConversionsQueryString(params)}`
      }),
      transformResponse: (response) => response || {
        message: '',
        success: 'false',
        summary: {},
      }
    }),

    getSubscriptionTrialConversionsByDevice: builder.query({
      query: (params = {}) => ({
        url: `/analytics/subscription/trial-conversions/by-device?${buildTrialConversionsQueryString(params)}`
      }),
      transformResponse: (response) => response?.data || {
        deviceConversionRates: [],
      }
    }),

    getSubscriptionTrialConversionsByCountry: builder.query({
      query: (params = {}) => ({
        url: `/analytics/subscription/trial-conversions/by-country?${buildTrialConversionsQueryString(params)}`
      }),
      transformResponse: (response) => response?.data || {
        countryPerformance: [],
        countryFunnel: [],
        currencySymbol: '$',
      }
    }),

    getSubscriptionTrialConversionsCancellation: builder.query({
      query: (params = {}) => ({
        url: `/analytics/subscription/trial-conversions/cancellation?${buildTrialConversionsQueryString(params)}`
      }),
      transformResponse: (response) => response?.data || {
        cancellationTiming: [],
        cancellationByPlan: [],
        overallCancellationByPlan: {
          planName: 'Overall',
          trials: 0,
          cancellations: 0,
          cancelRate: 0,
          mostCommonCancelTime: '--',
        },
      }
    }),

    getSubscriptionTrialConversionsUserList: builder.query({
      query: (params = {}) => ({
        url: `/analytics/subscription/trial-conversions/user-list?${buildTrialConversionsQueryString(params)}`
      }),
      transformResponse: (response) => response?.data || {
        userList: [],
        summary: {
          totalUsers: 0,
          converted: 0,
          cancelled: 0,
          inTrial: 0,
          expiredTrial: 0,
        },
        statusCounts: {},
        pagination: {
          page: 1,
          pageSize: 10,
          totalItems: 0,
          totalPages: 1,
          showingFrom: 0,
          showingTo: 0,
        },
      }
    }),

    getPayPerViewReportGraphLegacy: builder.query({
      query: ({ pubId, filter }) => {
        const startDate = filter?.start_date
          ? moment(filter.start_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = filter?.end_date
          ? moment(filter.end_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');
        const roleId = localStorage.getItem('role_id');
        const contentPartnerId = localStorage.getItem('contentPartnerId');
        let url = `/graph/payPerView?start_date=${startDate}&end_date=${endDate}`;
        if (roleId === '8' && contentPartnerId) {
          url += `&content_partner_checked=1&content_partner_id=${contentPartnerId}`;
        }
        return {
          url,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data || []
    }),

    getPayPerViewReportDefaultLegacy: builder.query({
      query: ({ pubId }) => {
        const startDate = moment().clone().startOf('month').format('YYYY-MM-DD');
        const endDate = moment().clone().endOf('month').format('YYYY-MM-DD');
        const roleId = localStorage.getItem('role_id');
        const contentPartnerId = localStorage.getItem('contentPartnerId');
        let url = `/report/payPerView?start_date=${startDate}&end_date=${endDate}`;
        if (roleId === '8' && contentPartnerId) {
          url += `&content_partner_checked=1&content_partner_id=${contentPartnerId}`;
        }
        return {
          url,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['PayPerViewReport']
    }),

    getPayPerViewReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams();
        const startDate = moment(data?.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        const endDate = moment(data?.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        params.append('start_date', startDate);
        params.append('end_date', endDate);

        if (data?.isAppSelected) {
          params.append('app_checked', '1');
          if (data?.appType?.id !== null && data?.appType?.id) {
            params.append('app_id', data.appType.app_type);
          }
        }

        if (data?.isCountrySelected) {
          params.append('country_checked', '1');
          if (data?.country?.value && data.country.value !== 1) {
            params.append('country', data.country.code);
          }
        }

        if (data?.isContentPartnerSelected) {
          params.append('content_partner_checked', '1');
          if (data?.contentPartner?.content_partner_id) {
            params.append('content_partner_id', data.contentPartner.content_partner_id);
          }
        }

        if (localStorage.getItem('role_id') == 8) {
          params.append('content_partner_checked', '1');
          params.append('content_partner_id', localStorage.getItem('contentPartnerId'));
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.video?.video_id) {
          params.append('video_id', data.video.video_id);
        }

        if (data?.show?.show_id) {
          params.append('show_id', `'${data.show.show_id}'`);
        }

        return {
          url: `/report/payPerView?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['PayPerViewReport']
    }),

    getRenewalGraphLegacy: builder.query({
      query: ({ pubId }) => {
        const startDate = moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');

        return {
          url: `/graph/renewal?start_date=${startDate}&end_date=${endDate}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data || []
    }),

    getRenewalReportDefaultLegacy: builder.query({
      query: ({ pubId, currencyId }) => {
        const startDate = moment().subtract(1, 'month').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate
        });

        if (currencyId) {
          params.append('currency_id', currencyId);
        }

        return {
          url: `/report/renewal?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['RenewalReport']
    }),

    getRenewalReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams();
        const startDate = moment(data?.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        const endDate = moment(data?.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        params.append('start_date', startDate);
        params.append('end_date', endDate);

        if (data?.isAppSelected) {
          params.append('app_checked', '1');
          if (data?.appType?.id !== null && data?.appType?.id) {
            params.append('app_id', data.appType.app_type);
          }
        }

        if (data?.isCountrySelected) {
          params.append('country_checked', '1');
          if (data?.country?.value && data.country.value !== 1) {
            params.append('country', data.country.code);
          }
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        if (data?.currencyValue?.value) {
          params.append('currency_id', data.currencyValue.value);
        }

        if (localStorage.getItem('role_id') == 8) {
          params.append('content_partner_checked', '1');
          params.append('content_partner_id', localStorage.getItem('contentPartnerId'));
        }

        return {
          url: `/report/renewal?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['RenewalReport']
    }),

    getNonRenewalGraphLegacy: builder.query({
      query: ({ pubId }) => {
        const startDate = moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        return {
          url: `/graph/nonrenewal?start_date=${startDate}&end_date=${endDate}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data || []
    }),

    getNonRenewalReportDefaultLegacy: builder.query({
      query: ({ pubId }) => {
        const startDate = moment().subtract(1, 'month').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        return {
          url: `/report/nonrenewal?start_date=${startDate}&end_date=${endDate}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['NonRenewalReport']
    }),

    getNonRenewalReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams();
        const startDate = moment(data?.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        const endDate = moment(data?.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        params.append('start_date', startDate);
        params.append('end_date', endDate);

        if (data?.isAppSelected) {
          params.append('app_checked', '1');
          if (data?.appType?.id !== null && data?.appType?.id) {
            params.append('app_id', data.appType.app_type);
          }
        }

        if (data?.isCountrySelected) {
          params.append('country_checked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.code) {
            params.append('country', data.country.code);
          }
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        return {
          url: `/report/nonrenewal?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['NonRenewalReport']
    }),

    getCancellationGraphLegacy: builder.query({
      query: ({ pubId, startDate, endDate }) => {
        let resolvedStartDate = startDate || moment().subtract(7, 'day').format('YYYY-MM-DD');
        let resolvedEndDate = endDate || moment().format('YYYY-MM-DD');

        if (resolvedStartDate > resolvedEndDate) {
          const tempDate = resolvedStartDate;
          resolvedStartDate = resolvedEndDate;
          resolvedEndDate = tempDate;
        }

        return {
          url: `/graph/cancel?pub_id=${pubId}&start_date=${resolvedStartDate}&end_date=${resolvedEndDate}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data || []
    }),

    getCancellationReportDefaultLegacy: builder.query({
      query: ({ pubId, currencyId }) => {
        const startDate = moment().subtract(1, 'month').format('YYYY-MM-DD');
        const endDate = moment().add(1, 'day').format('YYYY-MM-DD');
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate
        });

        if (currencyId) {
          params.append('currency_id', currencyId);
        }

        return {
          url: `/report/cancel?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['CancellationReport']
    }),

    getCancellationReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams();
        const startDate = moment(data?.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        const endDate = moment(data?.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        params.append('start_date', startDate);
        params.append('end_date', endDate);

        if (data?.isAppSelected) {
          params.append('app_checked', '1');
          if (data?.appType?.id !== null && data?.appType?.id) {
            params.append('app_id', data.appType.app_type);
          }
        }

        if (data?.isCountrySelected) {
          params.append('country_checked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.code) {
            params.append('country', data.country.code);
          }
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        if (data?.currencyValue?.value) {
          params.append('currency_id', data.currencyValue.value);
        }

        if (localStorage.getItem('role_id') == 8) {
          params.append('content_partner_checked', '1');
          params.append('content_partner_id', localStorage.getItem('contentPartnerId'));
        }

        return {
          url: `/report/cancel?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['CancellationReport']
    }),

    getCouponRevenueGraphLegacy: builder.query({
      query: ({ pubId, filter = {} }) => {
        const startDate = filter?.start_date
          ? moment(filter.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
          : moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = filter?.end_date
          ? moment(filter.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');

        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          transaction_type: '0',
          coupon_checked: '1',
          graph_checked: '1'
        });

        return {
          url: `/report/couponSubscriptionRevenue?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || []
    }),

    getRevenueReportGraphLegacy: builder.query({
      query: ({ pubId, filter = {} }) => {
        const startDate = filter?.start_date
          ? moment(filter.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
          : moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = filter?.end_date
          ? moment(filter.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');

        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate
        });

        return {
          url: `/graph/subscriptionRevenue?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      providesTags: ['RevenueReport']
    }),

    getRevenueReportDefaultLegacy: builder.query({
      query: ({ pubId, currencyId }) => {
        const startDate = moment().subtract(1, 'month').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');

        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          transaction_type: '0'
        });

        if (currencyId) {
          params.append('currency_id', currencyId);
        }

        return {
          url: `/report/subscriptionRevenue?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      providesTags: ['RevenueReport']
    }),

    getRevenueReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams();

        params.append('start_date', moment(data?.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD'));
        params.append('end_date', moment(data?.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD'));

        if (data?.transaction?.value !== 1) {
          params.append('transaction_type', String(data?.transaction?.id || 0));
        } else {
          params.append('transaction_type', '0');
        }

        if (data?.isAppSelected) {
          params.append('app_checked', '1');
          if (data?.appType?.id) {
            params.append('app_id', data.appType.app_type);
          }
        }

        if (data?.isCountrySelected) {
          params.append('country_checked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.code) {
            params.append('country', data.country.code);
          }
        }

        if (data?.isCitySelected) {
          params.append('city_checked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.show?.show_id) {
          params.append('show_id', `'${data.show.show_id}'`);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        if (data?.currencyValue?.value) {
          params.append('currency_id', data.currencyValue.value);
        }

        if (localStorage.getItem('role_id') == 8) {
          params.append('content_partner_checked', '1');
          params.append('content_partner_id', localStorage.getItem('contentPartnerId'));
        }

        return {
          url: `/report/subscriptionRevenue?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      providesTags: ['RevenueReport']
    }),

    getCouponRevenueReportDefaultLegacy: builder.query({
      query: ({ pubId }) => {
        const startDate = moment().subtract(30, 'day').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');

        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          transaction_type: '0',
          coupon_checked: '1'
        });

        return {
          url: `/report/couponSubscriptionRevenue?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || []
    }),

    getCouponRevenueReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams();

        params.append('start_date', moment(data?.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD'));
        params.append('end_date', moment(data?.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD'));

        if (data?.transaction?.value !== 1) {
          params.append('transaction_type', String(data?.transaction?.id || 0));
        } else {
          params.append('transaction_type', '0');
        }

        if (data?.isAppSelected) {
          params.append('app_checked', '1');
          if (data?.appType?.id) {
            params.append('app_id', data.appType.app_type);
          }
        }

        if (data?.isCountrySelected) {
          params.append('country_checked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.code) {
            params.append('country', data.country.code);
          }
        }

        if (data?.isCitySelected) {
          params.append('city_checked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isCouponChecked) {
          params.append('coupon_checked', '1');
          if (data?.coupon?.value) {
            params.append('coupon_id', data.coupon.value);
          }
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.show?.show_id) {
          params.append('show_id', `'${data.show.show_id}'`);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        if (data?.currencyValue?.value) {
          params.append('currency_id', data.currencyValue.value);
        }

        return {
          url: `/report/couponSubscriptionRevenue?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || []
    }),

    getRefundDataGraphLegacy: builder.query({
      query: ({ pubId, filter = {} }) => {
        const startDate = filter?.start_date
          ? moment(filter.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
          : moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = filter?.end_date
          ? moment(filter.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');
        const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
        return {
          url: `/graph/refund?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      providesTags: ['RefundReport']
    }),

    getRefundReportDefaultLegacy: builder.query({
      query: ({ pubId }) => {
        const startDate = moment().subtract(1, 'month').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
        return {
          url: `/report/refund?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || []
    }),

    getRefundReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams();
        params.append('start_date', moment(data?.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD'));
        params.append('end_date', moment(data?.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD'));

        if (data?.isAppSelected) {
          params.append('app_checked', '1');
          if (data?.appType?.id) {
            params.append('app_id', data.appType.app_type);
          }
        }

        if (data?.isCountrySelected) {
          params.append('country_checked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.code) {
            params.append('country', data.country.code);
          }
        }

        if (data?.isCitySelected) {
          params.append('city_checked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        return {
          url: `/report/refund?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || []
    }),

    getContentPartnerRevenueReportDefaultLegacy: builder.query({
      query: ({ pubId, channels }) => {
        const { effectivePubId, selectedChannel, params } = getContentPartnerRevenueLegacyRequestData({
          pubId,
          channels,
          isDefault: true
        });

        return {
          url: `/report/contentPartnerRevenue?${params.toString()}`,
          headers: getLegacyReportHeaders(effectivePubId, selectedChannel?.channel_id)
        };
      }
    }),

    getContentPartnerRevenueGraphLegacy: builder.query({
      query: ({ pubId, channels, filter }) => {
        const { effectivePubId, selectedChannel, params } = getContentPartnerRevenueLegacyRequestData({
          pubId,
          data: filter,
          channels
        });

        return {
          url: `/report/contentPartnerRevenue?${params.toString()}`,
          headers: getLegacyReportHeaders(effectivePubId, selectedChannel?.channel_id)
        };
      }
    }),

    getContentPartnerRevenueReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const { effectivePubId, selectedChannel, params } = getContentPartnerRevenueLegacyRequestData({
          pubId,
          data,
          channels: data?.channel?.channel_id ? [data.channel] : [],
          includeChannel: false
        });

        return {
          url: `/report/contentPartnerRevenue?${params.toString()}`,
          headers: getLegacyReportHeaders(effectivePubId, selectedChannel?.channel_id)
        };
      }
    }),

    getSubscribedUserGraphLegacy: builder.query({
      query: ({ pubId }) => {
        const startDate = moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');

        return {
          url: `/graph/subscribedUser?start_date=${startDate}&end_date=${endDate}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data || []
    }),

    getSubscribedUserReportDefaultLegacy: builder.query({
      query: ({ pubId }) => {
        const startDate = moment().subtract(1, 'month').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');

        return {
          url: `/report/subscribedUser?start_date=${startDate}&end_date=${endDate}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['SubscribedUsersReport']
    }),

    getSubscribedUserReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams();

        const startDate = moment(data?.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        const endDate = moment(data?.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        params.append('start_date', startDate);
        params.append('end_date', endDate);

        if (data?.status?.id) {
          params.append('status', data?.status?.id);
        }

        if (data?.isAppSelected) {
          params.append('app_checked', '1');
          if (data?.appType?.id !== null && data?.appType?.id) {
            params.append('app_id', data.appType.app_type);
          }
        }

        if (data?.isCountrySelected) {
          params.append('country_checked', '1');
          if (data?.country?.value && data.country.value !== 1) {
            params.append('country', data.country.code);
          }
        }

        if (data?.isContentPartnerSelected) {
          params.append('content_partner_checked', '1');
          if (data?.contentPartner?.content_partner_id) {
            params.append('content_partner_id', data.contentPartner.content_partner_id);
          }
        }

        if (localStorage.getItem('role_id') == 8) {
          params.append('content_partner_checked', '1');
          params.append('content_partner_id', localStorage.getItem('contentPartnerId'));
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.subscription?.subscription_id) {
          params.append('subscription_id', data.subscription.subscription_id);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        if (data?.video?.video_id) {
          params.append('video_id', data.video.video_id);
        }

        if (data?.show?.show_id) {
          params.append('show_id', `'${data.show.show_id}'`);
        }

        return {
          url: `/report/subscribedUser?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data?.[0] || [],
      providesTags: ['SubscribedUsersReport']
    }),

    getSubscribedUserVideoDropdownLegacy: builder.query({
      query: ({ pubId, data }) => {
        const startDate = moment(data?.start_date, 'MM-DD-YYYY').add(1, 'day').format('YYYY-MM-DD');
        const endDate = moment(data?.end_date, 'MM-DD-YYYY').add(1, 'day').format('YYYY-MM-DD');
        return {
          url: `/video/list?type=subscribed_users_dropdown&start_date=${startDate}&end_date=${endDate}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      },
      transformResponse: (response) => response?.data || { shows: [], videos: [] }
    }),

    getSubscriptionPaymentModesLegacy: builder.query({
      query: () => ({
        url: '/subscription/payment/mode',
        headers: {
          'access-token': localStorage.getItem('access-token'),
          pubid: localStorage.getItem('pub_id')
        }
      }),
      transformResponse: (response) => response?.data || []
    }),

    getContentPartnerListLegacy: builder.query({
      query: ({ channelId } = {}) => {
        const headers = {
          'access-token': localStorage.getItem('access-token'),
          pubid: localStorage.getItem('pub_id')
        };
        if (channelId) {
          headers.channelid = channelId;
        }
        return {
          url: '/contentPartner/list',
          headers
        };
      },
      transformResponse: (response) => response?.data || []
    }),

    getCurrencyListLegacy: builder.query({
      query: () => ({
        url: '/currency/list',
        headers: {
          'access-token': localStorage.getItem('access-token'),
          pubid: localStorage.getItem('pub_id')
        }
      }),
      transformResponse: (response) => response?.data || []
    }),

    // AI-generated viewership insights (POST to external AI service)
    getViewershipInsights: builder.query({
      queryFn: async ({ pubId, startDate, endDate }) => {
        try {
          const response = await axios.post(
            `${AI_BASE_URL}/insights`,
            { publisher_id: pubId, start_time: startDate, end_time: endDate },
            { headers: { 'Content-Type': 'application/json' } }
          );
          return { data: response.data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      }
    }),

    // AI formatting for engagement metric text
    formatEngagementMetrics: builder.mutation({
      queryFn: async (payload) => {
        try {
          const response = await axios.post(
            `${AI_BASE_URL}/format-engaging`,
            payload,
            { headers: { 'Content-Type': 'application/json' } }
          );
          return { data: response.data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      }
    }),

    // Subscription revenue data
    getSubscriptionRevenue: builder.query({
      query: ({ pubId, startDate, endDate }) => ({
        url: `/report/subscriptionRevenue?startDate=${startDate}&endDate=${endDate}&publisher_id=${pubId}`
      })
    }),

    // Subscription data (new subscribers, cancellations)
    getSubscriptionData: builder.query({
      query: ({ pubId, roleId, channelId, startDate, endDate }) => {
        let url = `/report/subscriptionData?startDate=${startDate}&endDate=${endDate}&publisher_id=${pubId}&role_id=${roleId}`;
        if (channelId) url += `&channel_id=${channelId}`;
        return { url, headers: { channelid: channelId || '' } };
      }
    }),

    // Subscription dashboard graph data
    getSubscriptionHomeGraph: builder.query({
      query: ({ startDate, endDate, channelId }) => ({
        url: `/graph/home?start_date=${startDate}&end_date=${endDate}`,
        headers: { channelid: channelId || '' }
      })
    }),

    // AVOD data with full filter support (revenue, cpm, fillrate, requests, impressions)
    getAvodDataFiltered: builder.query({
      query: ({ pubId, channelId, prevStartDate, curEndDate, appId, app, countryName, cityName, showId, videoId, contentPartnerId }) => {
        let url = `/report/revenue?startDate=${prevStartDate}&endDate=${curEndDate}&publisherchecked=1&publisher_id=${pubId}`;
        if (channelId) url += `&channelchecked=1&channel_id=${channelId}`;
        if (appId) url += `&appchecked=1&app_id=${appId}${app ? `&app_name=${encodeURIComponent(app)}` : ''}`;
        if (countryName) url += `&countrychecked=1&country=${encodeURIComponent(countryName)}`;
        if (cityName) url += `&citychecked=1&city=${encodeURIComponent(cityName)}`;
        if (showId) url += `&showchecked=1&show_id=${showId}`;
        if (videoId) url += `&videochecked=1&video_id=${videoId}`;
        if (contentPartnerId) url += `&content_partner_checked=1&content_partner_id=${contentPartnerId}`;
        const defaultContentPartnerId = localStorage.getItem('contentPartnerId');
        if (!contentPartnerId && defaultContentPartnerId) url += `&content_partner_checked=1&content_partner_id=${defaultContentPartnerId}`;
        return { url, headers: { channelid: channelId || '' } };
      }
    }),

    // AVOD data (revenue, cpm, fillrate, requests, impressions)
    getAvodData: builder.query({
      query: ({ pubId, channelId, startDate, endDate }) => {
        let url = `/report/revenue?startDate=${startDate}&endDate=${endDate}&publisherchecked=1&publisher_id=${pubId}`;
        if (channelId) url += `&channelchecked=1&channel_id=${channelId}`;

        const contentPartnerId = localStorage.getItem('contentPartnerId');
        if (contentPartnerId) {
          url += `&content_partner_checked=1&showchecked=1&videochecked=1&content_partner_id=${contentPartnerId}`;
        }

        return {
          url,
          headers: { channelid: channelId || '' }
        };
      }
    }),

    // AI Engagement Metrics (performance data)
    getAIEngagementMetrics: builder.query({
      query: ({ startDate, endDate, pubId }) => ({
        url: `/ai/performance/engagement?start_date=${startDate}&end_date=${endDate}`,
        headers: { pubid: pubId }
      }),
      transformResponse: (response) => response?.data
    }),

    // Most Loved AI-Recommended Categories
    getMostLovedCategories: builder.query({
      query: ({ pubId }) => ({
        url: `/ai/performance/trendingGenres`,
        headers: { pubid: pubId }
      }),
      transformResponse: (response) => response?.data
    }),

    // Active AI Recommendations Users
    getActiveAIRecommendations: builder.query({
      query: ({ channelId }) => ({
        url: `/ai/performance/activeUsersWithAIRecommendations`,
        headers: { channelid: channelId }
      }),
      transformResponse: (response) => response?.data ?? 0
    }),

    // Top 10 AI-Recommended Videos
    getTop10AIRecommendedVideos: builder.query({
      query: ({ startDate, endDate, pubId }) => ({
        url: `/ai/performance/topRecommendedVideos?start_date=${startDate}&end_date=${endDate}`,
        headers: { pubid: pubId }
      }),
      transformResponse: (response) => response?.data
    }),

    // App engagement (session) report
    getAppEngage: builder.query({
      query: ({ pubId, channelId, startDate, endDate, offset = 0, appId = '', countryChecked = 0, country = '', cityChecked = 0, city = '' }) => {
        let url = `/report/appSession?startDate=${startDate}&endDate=${endDate}&offset=${offset}&appchecked=1`;
        url += pubId ? `&publisherchecked=1&publisher_id=${pubId}` : '';
        url += channelId ? `&channelchecked=1&channel_id=${channelId}` : '';
        url += appId ? `&app_id=${appId}` : '';
        url += cityChecked === 1 ? `&citychecked=1${city ? `&city=${encodeURIComponent(city)}` : ''}` : '';
        url += countryChecked === 1 ? `&countrychecked=1${country ? `&country=${encodeURIComponent(country)}` : ''}` : '';
        return { url, headers: { pubid: pubId } };
      }
    }),

    // Legacy completed video analytics report
    getCompletedVideoReportLegacy: builder.query({
      query: ({ pubId, filter, channel }) => {
        const {
          startDate,
          endDate,
          params,
          effectivePubId
        } = getCompletedVideoLegacyRequestData({ pubId, filter, channel });

        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            queryParams.append(key, String(value));
          }
        });

        const querySuffix = queryParams.toString();
        const url = `/report/videocompleted?startDate=${startDate}&endDate=${endDate}${querySuffix ? `&${querySuffix}` : ''}`;

        return {
          url,
          headers: getLegacyReportHeaders(effectivePubId, params?.channel_id)
        };
      }
    }),

    // Legacy popup percentage details for a selected video row
    getCompletedVideoPercentageLegacy: builder.query({
      query: ({ pubId, startDate, endDate, channelId, videoId }) => {
        const formattedStartDate = formatLegacyDate(startDate);
        const formattedEndDate = formatLegacyDate(endDate);

        return {
          url: `/report/videocompleted?publisherchecked=1&publisher_id=${pubId}&startDate=${formattedStartDate}&endDate=${formattedEndDate}&channelchecked=1&channel_id=${channelId}&video_checked=1&video_id=${videoId}`,
          headers: getLegacyReportHeaders(pubId, channelId)
        };
      },
      transformResponse: (response) => (response?.success ? response : false)
    }),

    // Community active users filter report
    getCommunityActiveUsersReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const effectivePubId = data?.publishers?.pubid || pubId;
        const startDate = formatLegacyDate(data?.start_date);
        const endDate = formatLegacyDate(data?.end_date);
        const isUniqueSelected = Boolean(data?.isUniqueSelected);
        const basePath = isUniqueSelected
          ? '/report/activeCommunityUsersAggregate'
          : '/report/activeCommunityUsers';

        const params = new URLSearchParams({
          publisherchecked: '1',
          publisher_id: String(effectivePubId || ''),
          startDate,
          endDate
        });

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.label) {
            params.append('country', data.country.label);
          }
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) params.append('app_id', data.appType.id);
          if (data?.appType?.label && data.appType.label !== 'All') {
            params.append('app_name', data.appType.label.trim());
          }
        }

        if (data?.isChannelSelected) {
          params.append('channelchecked', '1');
          if (data?.channel?.channel_id) {
            params.append('channel_id', data.channel.channel_id);
          }
        }

        if (data?.subType?.subscription_type_id) {
          params.append('subscription_type', data.subType.subscription_type_id);
        }

        if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
          params.append('mode_of_payment', `'${data.paymentMode.label}'`);
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isMonthlySelected) params.append('monthly_checked', '1');
        if (data?.isAggregateSelected) params.append('aggregate_checked', '1');

        return {
          url: `${basePath}?${params.toString()}`,
          headers: getLegacyReportHeaders(effectivePubId, data?.channel?.channel_id)
        };
      }
    }),

    // Community active users graph data
    getTotalCommunityActiveUsersGraphLegacy: builder.query({
      query: ({ pubId, channels, filter }) => {
        let startDate = formatLegacyDate(filter?.start_date) || formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        let endDate = formatLegacyDate(filter?.end_date) || formatLegacyDate(new Date());
        if (startDate > endDate) {
          const temp = startDate;
          startDate = endDate;
          endDate = temp;
        }

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData) {
          params.append('channelchecked', '1');
          if (channelData.channel_id) {
            params.append('channel_id', channelData.channel_id);
          }
        }

        return {
          url: `/report/activeCommunityUsers?${params.toString()}`,
          headers: { 'access-token': localStorage.getItem('access-token'), pubid: pubId }
        };
      }
    }),

    // Community active users default (last 7 days)
    getTotalCommunityActiveUsersDefaultLegacy: builder.query({
      query: ({ pubId, channels }) => {
        const endDate = formatLegacyDate(new Date());
        const startDate = formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData) {
          params.append('channelchecked', '1');
          if (channelData.channel_id) {
            params.append('channel_id', channelData.channel_id);
          }
        }

        return {
          url: `/report/activeCommunityUsers?${params.toString()}`,
          headers: { 'access-token': localStorage.getItem('access-token'), pubid: pubId }
        };
      }
    }),

    getCommunityPostAnalyticsGraph: builder.query({
      query: ({ pubId, channels, filter }) => {
        let startDate = formatLegacyDate(filter?.start_date) || formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        let endDate = formatLegacyDate(filter?.end_date) || formatLegacyDate(new Date());

        if (startDate > endDate) {
          const temp = startDate;
          startDate = endDate;
          endDate = temp;
        }

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelData.channel_id);
        }

        return {
          url: `/report/postViewAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(pubId, channelData?.channel_id)
        };
      }
    }),

    getCommunityPostAnalyticsDefault: builder.query({
      query: ({ pubId, data }) => {
        const startDate = formatLegacyDate(data?.start_date) || formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        const endDate = formatLegacyDate(data?.end_date) || formatLegacyDate(new Date());
        const channelId = data?.channel_id || data?.channel?.channel_id || null;

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          post_checked: '1',
          publisher_id: String(pubId || '')
        });

        if (data?.isMonthlySelected) {
          params.append('monthly_checked', '1');
        }

        if (data?.isAggregateSelected) {
          params.append('aggregate_checked', '1');
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) {
            params.append('app_id', data.appType.id);
          }
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.label && data.country.label !== 'All') {
            params.append('country', data.country.label);
          }
        }

        if (channelId) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelId);
        }

        return {
          url: `/report/postViewAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(pubId, channelId)
        };
      }
    }),

    getEventAnalyticsGraph: builder.query({
      query: ({ pubId, channels, filter }) => {
        let startDate = formatLegacyDate(filter?.start_date) || formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        let endDate = formatLegacyDate(filter?.end_date) || formatLegacyDate(new Date());

        if (startDate > endDate) {
          const temp = startDate;
          startDate = endDate;
          endDate = temp;
        }

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        if (filter?.isEventDropdownTouched) {
          params.append('eventchecked', '1');
          if (filter?.isLinearEventChecked && filter?.linearEvent?.id) {
            params.append('event_id', filter.linearEvent.id);
          }
        }

        const channelData = Array.isArray(channels) ? channels[0] : null;
        if (channelData?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelData.channel_id);
        }

        return {
          url: `/graph/eventAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(pubId, channelData?.channel_id)
        };
      }
    }),

    getEventAnalyticsDefault: builder.query({
      query: ({ pubId, data }) => {
        const effectivePubId = data?.publishers?.pubid || pubId;
        const startDate = formatLegacyDate(data?.start_date) || formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        const endDate = formatLegacyDate(data?.end_date) || formatLegacyDate(new Date());
        const channelId = data?.channel_id || data?.channel?.channel_id || null;

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(effectivePubId || '')
        });

        if (data?.isEventDropdownTouched) {
          params.append('eventchecked', '1');
          if (data?.isLinearEventChecked && data?.linearEvent?.id) {
            params.append('event_id', data.linearEvent.id);
          }
        }

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.label && data.country.label !== 'All') {
            params.append('country', data.country.label);
          }
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) {
            params.append('app_id', data.appType.id);
          }
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isVideoChecked) {
          params.append('videochecked', '1');
          if (data?.video?.video_id) {
            params.append('video_id', data.video.video_id);
          }
        }

        if (data?.isShowchecked) {
          params.append('showchecked', '1');
          if (data?.show?.show_id) {
            params.append('show_id', data.show.show_id);
          }
        }

        if (data?.isMonthlySelected) {
          params.append('monthly_checked', '1');
        }

        if (channelId) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelId);
        }

        return {
          url: `/report/eventAnalytics?${params.toString()}`,
          headers: getLegacyReportHeaders(effectivePubId, channelId)
        };
      }
    }),

    getEventAnalyticsLinearEventList: builder.query({
      query: ({ pubId }) => {
        const channelId = localStorage.getItem('currentChannel');
        const roleId = localStorage.getItem('role_id');
        const contentPartnerId = localStorage.getItem('contentPartnerId');

        return {
          url: '/linearEvent/list?type=schedule_dropdown',
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId,
            ...(channelId ? { channelid: channelId } : {}),
            ...(roleId ? { role_id: roleId } : {}),
            ...(contentPartnerId ? { content_partner_id: contentPartnerId } : {})
          }
        };
      }
    }),

    // Legacy hourly engagement report
    getHourlyEngagementLegacy: builder.query({
      query: ({ pubId, data }) => {
        const startDate = formatLegacyDate(data.dateRange?.[0]?.startDate);
        const endDate = formatLegacyDate(data.dateRange?.[0]?.endDate);

        const params = new URLSearchParams({
          publisherchecked: '1',
          publisher_id: String(pubId || ''),
          startDate,
          endDate
        });

        if (data.selectedTimeZone?.value) {
          params.append('timezone', data.selectedTimeZone.value);
        }

        if (data.filters_state?.appType?.id) {
          params.append('appchecked', '1');
          params.append('app_id', data.filters_state.appType.id);
        }

        if (data.filters_state?.country?.label) {
          params.append('countrychecked', '1');
          if (data.filters_state.country.label !== 'All') {
            params.append('country', data.filters_state.country.label);
          }
        }

        if (data.filters_state?.city?.label) {
          params.append('citychecked', '1');
          if (data.filters_state.city.label !== 'All') {
            params.append('city', data.filters_state.city.label);
          }
        }

        return {
          url: `/report/hourlyEngagement?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: pubId
          }
        };
      }
    }),

    // Category Video View Report
    getCategoryVideoViewReport: builder.query({
      query: ({ filter, pubid, channel }) => {
        const defaultEndDate = formatLegacyDate(new Date());
        const defaultStartDate = formatLegacyDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
        let startDate = formatLegacyDate(filter?.start_date) || defaultStartDate;
        let endDate = formatLegacyDate(filter?.end_date) || defaultEndDate;

        if (startDate > endDate) {
          const temp = startDate;
          startDate = endDate;
          endDate = temp;
        }

        const params = new URLSearchParams();
        params.append('publisherchecked', '1');
        
        if (filter?.isCategorySelected) params.append('categorychecked', '1');
        if (filter?.isAppSelected) params.append('appchecked', '1');
        if (filter?.isChannelsSelected) params.append('channelchecked', '1');
        if (filter?.isCountrySelected) params.append('countrychecked', '1');
        if (filter?.isCitySelected) params.append('citychecked', '1');
        if (filter?.isMonthlySelected) params.append('monthly_checked', '1');
        if (filter?.isAggregateSelected) params.append('aggregate_checked', '1');

        const effectivePubId = pubid || filter?.publishers?.pubid || localStorage.getItem('pub_id');
        if (effectivePubId) params.append('publisher_id', effectivePubId);
        if (filter?.appType?.id) params.append('app_id', filter.appType.id);
        if (filter?.category?.category_id) params.append('category_id', filter.category.category_id);

        const selectedChannel = Array.isArray(channel) && channel?.[0]?.channel_id
          ? channel[0]
          : filter?.channel;
        if (selectedChannel?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', selectedChannel.channel_id);
        }

        if (filter?.country?.label && filter.country.label !== 'All') {
          params.append('country', filter.country.label);
        }

        if (filter?.city?.label && filter.city.label !== 'All') {
          params.append('city', filter.city.label);
        }

        const roleId = localStorage.getItem('role_id');
        if (roleId) params.append('role_id', roleId);

        return {
          url: `/report/videocategoryviews?startDate=${startDate}&endDate=${endDate}&${params.toString()}`,
        };
      },
      providesTags: ['Viewership']
    }),

    getAvodRevenueReportGraphLegacy: builder.query({
      query: ({ pubId, filter = {} }) => {
        const startDate = filter?.start_date
          ? moment(filter.start_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = filter?.end_date
          ? moment(filter.end_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        if (filter?.channel?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', filter.channel.channel_id);
        }

        const contentPartnerId = localStorage.getItem('contentPartnerId');
        if (contentPartnerId) {
          params.append('content_partner_id', contentPartnerId);
          params.append('content_partner_checked', '1');
          params.append('videochecked', '1');
        }

        return {
          url: `/report/revenue?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: String(pubId || '')
          }
        };
      },
      transformResponse: (response) => response?.data || [],
      providesTags: ['RevenueReport']
    }),

    getAvodRevenueReportDefaultLegacy: builder.query({
      query: ({ pubId, channelId }) => {
        const startDate = moment().subtract(6, 'day').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || '')
        });

        if (channelId) {
          params.append('channelchecked', '1');
          params.append('channel_id', channelId);
        }

        const contentPartnerId = localStorage.getItem('contentPartnerId');
        if (contentPartnerId) {
          params.append('content_partner_id', contentPartnerId);
          params.append('content_partner_checked', '1');
          params.append('videochecked', '1');
        }

        return {
          url: `/report/revenue?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: String(pubId || '')
          }
        };
      },
      providesTags: ['RevenueReport']
    }),

    getAvodRevenueReportFilterLegacy: builder.query({
      query: ({ pubId, data }) => {
        const effectivePubId = data?.publishers?.pubid || pubId;
        const startDate = moment(data?.start_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD');
        const endDate = moment(data?.end_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD');

        const params = new URLSearchParams({
          startDate,
          endDate,
          publisherchecked: '1',
          publisher_id: String(effectivePubId || '')
        });

        if (data?.isChannelSelected && data?.channel?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', data.channel.channel_id);
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) params.append('app_id', data.appType.id);
          if (data?.appType?.label && data.appType.label !== 'All') {
            params.append('app_name', data.appType.label);
          }
        }

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.label) {
            params.append('country', data.country.label);
          }
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isShowchecked) {
          params.append('showchecked', '1');
          if (data?.show?.id) params.append('show_id', data.show.id);
        }

        if (data?.isVideoChecked) {
          params.append('videochecked', '1');
          if (data?.video?.id) params.append('video_id', data.video.id);
        }

        if (data?.isContentPartnerSelected) {
          params.append('content_partner_checked', '1');
          if (data?.contentPartner?.content_partner_id) {
            params.append('content_partner_id', data.contentPartner.content_partner_id);
          }
        }

        if (localStorage.getItem('role_id') === '8') {
          params.append('content_partner_checked', '1');
          const cpId = localStorage.getItem('contentPartnerId');
          if (cpId) params.append('content_partner_id', cpId);
        }

        if (data?.isMonthlySelected) params.append('monthly_checked', '1');
        if (data?.isAggregateSelected) params.append('aggregate_checked', '1');

        return {
          url: `/report/revenue?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token'),
            pubid: String(effectivePubId || '')
          }
        };
      },
      providesTags: ['RevenueReport']
    }),

    getDemandReportDefaultLegacy: builder.query({
      query: ({ pubId, channels, filter = {} }) => {
        let startDate = filter?.start_date
          ? moment(filter.start_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : moment().subtract(6, 'day').format('YYYY-MM-DD');
        let endDate = filter?.end_date
          ? moment(filter.end_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD');

        if (!startDate || startDate === 'Invalid date') {
          startDate = moment().subtract(6, 'day').format('YYYY-MM-DD');
        }
        if (!endDate || endDate === 'Invalid date') {
          endDate = moment().format('YYYY-MM-DD');
        }

        const params = new URLSearchParams({
          from: startDate,
          to: endDate,
          publisherchecked: '1',
          publisher_id: String(pubId || ''),
          demandchecked: '1'
        });

        if (filter?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (filter?.country?.label && filter.country.label !== 'All') {
            params.append('country', filter.country.label);
          }
        }

        if (filter?.isAppSelected) {
          params.append('appchecked', '1');
          if (filter?.appType?.value) {
            params.append('app_name', filter.appType.value);
          }
        }

        if (filter?.isPartnerSelected) {
          params.append('partnerchecked', '1');
          if (filter?.partner?.partner_id && filter.partner.partner_id !== 0) {
            params.append('partner_id', filter.partner.partner_id);
          }
        }

        if (filter?.isDemandSelected && filter?.demand?.demand_source_id && filter.demand.demand_source_id !== 0) {
          params.append('dspid', filter.demand.demand_source_id);
        }

        if (filter?.isCitySelected) {
          params.append('citychecked', '1');
          if (filter?.city?.label && filter.city.label !== 'All') {
            params.append('city', filter.city.label);
          }
        }

        if (filter?.isMonthlySelected) {
          params.append('monthly', '1');
          params.append('monthly_checked', '1');
        }
        if (filter?.isAggregateSelected) {
          params.append('aggregate_checked', '1');
        }

        const selectedChannel = Array.isArray(channels) && channels.length > 0
          ? channels[0]
          : filter?.channel;
        if (selectedChannel?.channel_id) {
          params.append('channelchecked', '1');
          params.append('channel_id', selectedChannel.channel_id);
        }

        return {
          url: `/report/demand?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token') || '',
            pubid: String(pubId || '')
          }
        };
      },
      providesTags: ['DemandReport']
    }),

    getDemandReportFilterLegacy: builder.query({
      query: ({ pubId, data = {} }) => {
        const startDate = moment(data?.start_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD');
        const endDate = moment(data?.end_date, ['MM-DD-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD');
        const effectivePubId = data?.publishers?.pubid || pubId;

        const params = new URLSearchParams({
          from: startDate,
          to: endDate,
          publisherchecked: '1',
          publisher_id: String(effectivePubId || ''),
          demandchecked: '1'
        });

        if (data?.isCountrySelected) {
          params.append('countrychecked', '1');
          if (data?.country?.value && data.country.value !== 1 && data?.country?.label) {
            params.append('country', data.country.label);
          }
        }

        if (data?.isAppSelected) {
          params.append('appchecked', '1');
          if (data?.appType?.id) {
            params.append('app_id', data.appType.id);
          }
          if (data?.appType?.label && data.appType.label !== 'All') {
            params.append('app_name', data.appType.label);
          }
        }

        if (data?.isDemandSelected && data?.demand?.demand_source_id && data.demand.demand_source_id !== 0) {
          params.append('dspid', data.demand.demand_source_id);
        }

        if (data?.isPartnerSelected) {
          params.append('partnerchecked', '1');
          if (data?.partner?.partner_id && data.partner.partner_id !== 0) {
            params.append('partner_id', data.partner.partner_id);
          }
        }

        if (data?.isChannelSelected) {
          params.append('channelchecked', '1');
          if (data?.channel?.channel_id) {
            params.append('channel_id', data.channel.channel_id);
          }
        }

        if (data?.isCitySelected) {
          params.append('citychecked', '1');
          if (data?.city?.label && data.city.label !== 'All') {
            params.append('city', data.city.label);
          }
        }

        if (data?.isMonthlySelected) {
          params.append('monthly', '1');
          params.append('monthly_checked', '1');
        }
        if (data?.isAggregateSelected) {
          params.append('aggregate_checked', '1');
        }

        return {
          url: `/report/demand?${params.toString()}`,
          headers: {
            'access-token': localStorage.getItem('access-token') || '',
            pubid: String(effectivePubId || '')
          }
        };
      },
      providesTags: ['DemandReport']
    }),

    // Campaign report — date range + publisher filter
    getCampaignReportLegacy: builder.query({
      query: ({ pubId, startDate, endDate }) => {
        const accessToken = localStorage.getItem('access-token');
        return {
          url: `/report/campaign?from=${startDate}&to=${endDate}&publisherchecked=1&publisher_id=${pubId}&campaignchecked=1`,
          headers: {
            'access-token': accessToken,
            pubid: String(pubId || '')
          }
        };
      }
    }),

  })
});

export const {
  useGetVodAnalyticsQuery,
  useGetActiveUsersQuery,
  useGetActiveUsersStatsQuery,
  useGetNewUsersQuery,
  useLazyGetNewUsersQuery,
  useGetTotalUsersQuery,
  useGetLiveViewersQuery,
  useGetTopViewedQuery,
  useGetViewershipTopViewedQuery,
  useGetViewershipEngagementAndGenresQuery,
  useGetViewershipTopLiveChannelsQuery,
  useGetViewershipActiveAndNewUsersQuery,
  useGetViewershipContentPerformanceQuery,
  useGetViewershipLiveAnalyticsGraphQuery,
  useGetFastChannelsLegacyQuery,
  useLazyGetFastChannelsLegacyQuery,
  useGetFastAnalyticsGraphLegacyQuery,
  useLazyGetFastAnalyticsGraphLegacyQuery,
  useGetFastAnalyticsDefaultLegacyQuery,
  useLazyGetFastAnalyticsDefaultLegacyQuery,
  useGetFastAnalyticsFilterLegacyQuery,
  useLazyGetFastAnalyticsFilterLegacyQuery,
  useGetCategoryVideoViewReportQuery,
  useGetActiveUsersReportFilterLegacyQuery,
  useLazyGetActiveUsersReportFilterLegacyQuery,
  useGetUserDetailsReportFilterLegacyQuery,
  useLazyGetUserDetailsReportFilterLegacyQuery,
  useGetUserDetailsDefaultLegacyQuery,
  useLazyGetUserDetailsDefaultLegacyQuery,
  useGetTotalActiveUsersGraphLegacyQuery,
  useLazyGetTotalActiveUsersGraphLegacyQuery,
  useGetTotalActiveUsersDefaultLegacyQuery,
  useLazyGetTotalActiveUsersDefaultLegacyQuery,
  useGetNewsReportFilterLegacyQuery,
  useLazyGetNewsReportFilterLegacyQuery,
  useGetNewsAnalyticsDefaultLegacyQuery,
  useLazyGetNewsAnalyticsDefaultLegacyQuery,
  useGetNewsListLegacyQuery,
  useLazyGetNewsListLegacyQuery,
  useGetShowListLegacyQuery,
  useLazyGetShowListLegacyQuery,
  useGetShortsReportFilterLegacyQuery,
  useLazyGetShortsReportFilterLegacyQuery,
  useGetShortsAnalyticsDefaultLegacyQuery,
  useLazyGetShortsAnalyticsDefaultLegacyQuery,
  useGetShortsAnalyticsGraphLegacyQuery,
  useLazyGetShortsAnalyticsGraphLegacyQuery,
  useGetShortsListLegacyQuery,
  useLazyGetShortsListLegacyQuery,
  useGetErrorReportGraphLegacyQuery,
  useLazyGetErrorReportGraphLegacyQuery,
  useGetErrorReportDefaultLegacyQuery,
  useLazyGetErrorReportDefaultLegacyQuery,
  useGetErrorReportFilterLegacyQuery,
  useLazyGetErrorReportFilterLegacyQuery,
  useGetRatingReportDefaultQuery,
  useLazyGetRatingReportDefaultQuery,
  useGetRatingReportFilterQuery,
  useLazyGetRatingReportFilterQuery,
  useGetShowDropdownQuery,
  useLazyGetShowDropdownQuery,
  useGetVideoDropdownQuery,
  useLazyGetVideoDropdownQuery,
  useGetCollectionDropdownQuery,
  useLazyGetCollectionDropdownQuery,
  useGetFavoriteReportDefaultQuery,
  useLazyGetFavoriteReportDefaultQuery,
  useGetFavoriteReportFilterQuery,
  useLazyGetFavoriteReportFilterQuery,
  useGetNewsLetterReportDefaultQuery,
  useLazyGetNewsLetterReportDefaultQuery,
  useGetNewsLetterReportFilterQuery,
  useLazyGetNewsLetterReportFilterQuery,
  useGetDataUsageReportDefaultQuery,
  useLazyGetDataUsageReportDefaultQuery,
  useGetDataUsageReportFilterQuery,
  useLazyGetDataUsageReportFilterQuery,
  useGetAnalyticsFilterAppTypeQuery,
  useLazyGetAnalyticsFilterAppTypeQuery,
  useGetAnalyticsFilterSubscriptionListQuery,
  useLazyGetAnalyticsFilterSubscriptionListQuery,
  useGetAnalyticsFilterSubscriptionPlanListQuery,
  useLazyGetAnalyticsFilterSubscriptionPlanListQuery,
  useGetAnalyticsFilterSubscriptionTypeQuery,
  useLazyGetAnalyticsFilterSubscriptionTypeQuery,
  useGetAnalyticsFilterShowListQuery,
  useLazyGetAnalyticsFilterShowListQuery,
  useGetAnalyticsFilterCountryListQuery,
  useLazyGetAnalyticsFilterCountryListQuery,
  useGetAnalyticsFilterVideoListQuery,
  useLazyGetAnalyticsFilterVideoListQuery,
  useGetAnalyticsFilterChannelListQuery,
  useLazyGetAnalyticsFilterChannelListQuery,
  useGetAnalyticsFilterPublishersListQuery,
  useLazyGetAnalyticsFilterPublishersListQuery,
  useGetAnalyticsFilterCityListQuery,
  useLazyGetAnalyticsFilterCityListQuery,
  useGetAnalyticsFilterCategoryListQuery,
  useLazyGetAnalyticsFilterCategoryListQuery,
  useGetAnalyticsFilterDemandSourceListQuery,
  useLazyGetAnalyticsFilterDemandSourceListQuery,
  useGetAnalyticsFilterCouponsListQuery,
  useLazyGetAnalyticsFilterCouponsListQuery,
  useGetAvodTopDemandLegacyQuery,
  useGetAvodRevenueLegacyQuery,
  useGetSubscriptionTotalActiveLegacyQuery,
  useGetSubscriptionRevenueLegacyQuery,
  useGetSubscriptionReportByTypeLegacyQuery,
  useGetSubscriptionCancelledByTermsLegacyQuery,
  useGetSubscriptionPayPerViewLegacyQuery,
  useGetSubscriptionTrialConversionsQuery,
  useGetSubscriptionTrialConversionsOverviewQuery,
  useGetSubscriptionTrialConversionsEffectivenessQuery,
  useGetSubscriptionTrialConversionsByDeviceQuery,
  useGetSubscriptionTrialConversionsByCountryQuery,
  useGetSubscriptionTrialConversionsCancellationQuery,
  useGetSubscriptionTrialConversionsUserListQuery,
  useGetPayPerViewReportGraphLegacyQuery,
  useLazyGetPayPerViewReportGraphLegacyQuery,
  useGetPayPerViewReportDefaultLegacyQuery,
  useLazyGetPayPerViewReportDefaultLegacyQuery,
  useGetPayPerViewReportFilterLegacyQuery,
  useLazyGetPayPerViewReportFilterLegacyQuery,
  useGetRenewalGraphLegacyQuery,
  useGetRenewalReportDefaultLegacyQuery,
  useGetRenewalReportFilterLegacyQuery,
  useLazyGetRenewalReportFilterLegacyQuery,
  useGetNonRenewalGraphLegacyQuery,
  useLazyGetNonRenewalGraphLegacyQuery,
  useGetNonRenewalReportDefaultLegacyQuery,
  useLazyGetNonRenewalReportDefaultLegacyQuery,
  useGetNonRenewalReportFilterLegacyQuery,
  useLazyGetNonRenewalReportFilterLegacyQuery,
  useGetCancellationGraphLegacyQuery,
  useGetCancellationReportDefaultLegacyQuery,
  useGetCancellationReportFilterLegacyQuery,
  useGetCouponRevenueGraphLegacyQuery,
  useLazyGetCouponRevenueGraphLegacyQuery,
  useGetRevenueReportGraphLegacyQuery,
  useLazyGetRevenueReportGraphLegacyQuery,
  useGetRevenueReportDefaultLegacyQuery,
  useLazyGetRevenueReportDefaultLegacyQuery,
  useGetRevenueReportFilterLegacyQuery,
  useLazyGetRevenueReportFilterLegacyQuery,
  useGetRefundDataGraphLegacyQuery,
  useLazyGetRefundDataGraphLegacyQuery,
  useGetRefundReportDefaultLegacyQuery,
  useLazyGetRefundReportDefaultLegacyQuery,
  useGetRefundReportFilterLegacyQuery,
  useLazyGetRefundReportFilterLegacyQuery,
  useGetCouponRevenueReportDefaultLegacyQuery,
  useLazyGetCouponRevenueReportDefaultLegacyQuery,
  useGetCouponRevenueReportFilterLegacyQuery,
  useLazyGetCouponRevenueReportFilterLegacyQuery,
  useGetContentPartnerRevenueReportDefaultLegacyQuery,
  useLazyGetContentPartnerRevenueReportDefaultLegacyQuery,
  useGetContentPartnerRevenueGraphLegacyQuery,
  useLazyGetContentPartnerRevenueGraphLegacyQuery,
  useGetContentPartnerRevenueReportFilterLegacyQuery,
  useLazyGetContentPartnerRevenueReportFilterLegacyQuery,
  useGetSubscribedUserGraphLegacyQuery,
  useGetSubscribedUserReportDefaultLegacyQuery,
  useGetSubscribedUserReportFilterLegacyQuery,
  useLazyGetSubscribedUserReportFilterLegacyQuery,
  useGetSubscribedUserVideoDropdownLegacyQuery,
  useGetSubscriptionPaymentModesLegacyQuery,
  useGetContentPartnerListLegacyQuery,
  useLazyGetContentPartnerListLegacyQuery,
  useGetCurrencyListLegacyQuery,
  useGetViewershipInsightsQuery,
  useFormatEngagementMetricsMutation,
  useGetSubscriptionRevenueQuery,
  useGetSubscriptionDataQuery,
  useGetSubscriptionHomeGraphQuery,
  useGetAvodDataFilteredQuery,
  useLazyGetAvodDataFilteredQuery,
  useGetAvodDataQuery,
  useLazyGetAvodDataQuery,
  useGetAIEngagementMetricsQuery,
  useGetMostLovedCategoriesQuery,
  useGetActiveAIRecommendationsQuery,
  useGetTop10AIRecommendedVideosQuery,
  useGetAppEngageQuery,
  useLazyGetAppEngageQuery,
  useGetCompletedVideoReportLegacyQuery,
  useLazyGetCompletedVideoReportLegacyQuery,
  useGetCompletedVideoPercentageLegacyQuery,
  useLazyGetCompletedVideoPercentageLegacyQuery,
  useGetHourlyEngagementLegacyQuery,
  useLazyGetHourlyEngagementLegacyQuery,
  useGetCommunityPostAnalyticsGraphQuery,
  useLazyGetCommunityPostAnalyticsGraphQuery,
  useGetCommunityPostAnalyticsDefaultQuery,
  useLazyGetCommunityPostAnalyticsDefaultQuery,
  useGetEventAnalyticsGraphQuery,
  useLazyGetEventAnalyticsGraphQuery,
  useGetEventAnalyticsDefaultQuery,
  useLazyGetEventAnalyticsDefaultQuery,
  useGetEventAnalyticsLinearEventListQuery,
  useLazyGetEventAnalyticsLinearEventListQuery,
  useGetCommunityActiveUsersReportFilterLegacyQuery,
  useLazyGetCommunityActiveUsersReportFilterLegacyQuery,
  useGetTotalCommunityActiveUsersGraphLegacyQuery,
  useLazyGetTotalCommunityActiveUsersGraphLegacyQuery,
  useGetTotalCommunityActiveUsersDefaultLegacyQuery,
  useLazyGetTotalCommunityActiveUsersDefaultLegacyQuery,
  useGetAvodRevenueReportGraphLegacyQuery,
  useLazyGetAvodRevenueReportGraphLegacyQuery,
  useGetAvodRevenueReportDefaultLegacyQuery,
  useLazyGetAvodRevenueReportDefaultLegacyQuery,
  useGetAvodRevenueReportFilterLegacyQuery,
  useLazyGetAvodRevenueReportFilterLegacyQuery,
  useGetDemandReportDefaultLegacyQuery,
  useLazyGetDemandReportDefaultLegacyQuery,
  useGetDemandReportFilterLegacyQuery,
  useLazyGetDemandReportFilterLegacyQuery,
  useGetCampaignReportLegacyQuery,
  useLazyGetCampaignReportLegacyQuery,
} = analyticsApi;
